import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Avatar, Modal } from '../common/UIComponents';
import { NotificationBell } from '../layout/NotificationBell';
import type { ServiceWorkspaceActiveSection } from '../../utils/navigationActiveState';

type ServiceWorkspaceNavItem = {
  key: ServiceWorkspaceActiveSection;
  label: string;
  to: string;
};

type ServiceWorkspaceHeaderProps = {
  activeSection: ServiceWorkspaceActiveSection;
  activeSectionLabel: string;
  serviceName: string;
  userEmail?: string | null;
  avatarName: string;
  avatarSrc?: string | null;
  isMenuOpen: boolean;
  onToggleMenu: () => void;
  onCloseMenu: () => void;
  items: ServiceWorkspaceNavItem[];
  publicExitHref: string;
  compactMobileChrome?: boolean;
};

const menuItemBaseClass =
  'relative flex min-h-[46px] items-center rounded-xl px-4 py-3 text-sm font-medium transition-colors focus-outline';

export const ServiceWorkspaceHeader: React.FC<ServiceWorkspaceHeaderProps> = ({
  activeSection,
  activeSectionLabel,
  serviceName,
  userEmail,
  avatarName,
  avatarSrc,
  isMenuOpen,
  onToggleMenu,
  onCloseMenu,
  items,
  publicExitHref,
  compactMobileChrome = false,
}) => {
  const serviceItems = items.filter((item) => item.key !== 'community');
  const communityItems = items.filter((item) => item.key === 'community');
  const menuTriggerRef = useRef<HTMLButtonElement | null>(null);
  const menuInitialFocusRef = useRef<HTMLButtonElement | null>(null);

  return (
    <>
      <header
        data-testid="service-workspace-header"
        data-app-chrome="topbar"
        data-mobile-chrome={compactMobileChrome ? 'compact' : 'default'}
        className="sticky top-0 z-50 border-b border-primary-dark-300/20 bg-primary-dark-200/96 pt-[max(env(safe-area-inset-top),0px)] shadow-none backdrop-blur-sm"
      >
        <div className="px-3 [padding-left:max(env(safe-area-inset-left),0.75rem)] [padding-right:max(env(safe-area-inset-right),0.75rem)] sm:px-4 lg:px-6">
          <div
            className={`flex items-center gap-2.5 ${
              compactMobileChrome ? 'min-h-[2.75rem] sm:min-h-[3.625rem]' : 'min-h-[3.625rem]'
            }`}
          >
            <div
              data-testid="service-workspace-header-active-label"
              className={`max-w-[7.75rem] shrink-0 overflow-hidden text-ellipsis whitespace-nowrap text-[13px] font-semibold leading-5 text-primary-light-100 sm:max-w-[9rem] sm:text-[14px] ${
                compactMobileChrome ? 'max-w-[5.75rem] sm:max-w-[9rem]' : ''
              }`}
              title={activeSectionLabel}
            >
              {activeSectionLabel}
            </div>
            <div data-testid="service-workspace-header-service-name" className="min-w-0 flex-1">
              <span
                className={`block overflow-hidden text-ellipsis whitespace-nowrap font-medium leading-5 ${
                  compactMobileChrome
                    ? 'text-[12px] text-primary-light-400 sm:text-[13px] sm:text-primary-light-300'
                    : 'text-[13px] text-primary-light-300'
                }`}
                title={serviceName}
              >
                {serviceName}
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
              <NotificationBell />
              <button
                ref={menuTriggerRef}
                type="button"
                aria-label="Otwórz menu workspace serwisu"
                aria-expanded={isMenuOpen}
                aria-haspopup="dialog"
                onClick={onToggleMenu}
                className="rounded-full p-0.5 text-primary-light-300 transition-colors hover:text-ratuj-300 focus-outline"
              >
                <Avatar src={avatarSrc || undefined} name={avatarName} size="sm" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {isMenuOpen && typeof document !== 'undefined'
        ? createPortal(
            <Modal
              isOpen
              onClose={onCloseMenu}
              ariaLabel="Menu workspace serwisu"
              initialFocusRef={menuInitialFocusRef}
              returnFocusRef={menuTriggerRef}
              placement="right"
              overlayClassName="!z-[70] !bg-primary-dark-100/68"
              overlayTestId="service-workspace-menu-overlay"
              dialogTestId="service-workspace-menu-dialog"
              className="relative z-10 flex !h-full !max-h-none !w-[min(84vw,360px)] !max-w-[360px] flex-col !overflow-hidden !rounded-none !border-y-0 !border-r-0 !border-l-primary-dark-300/26"
            >
              <div className="flex items-start justify-between gap-3 border-b border-primary-dark-300/22 px-4 pb-4 pt-5">
                <div className="flex min-w-0 items-start gap-3">
                  <Avatar src={avatarSrc || undefined} name={avatarName} size="md" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-primary-light-100">
                      {serviceName}
                    </p>
                    {userEmail ? (
                      <p className="truncate text-xs text-primary-light-400">{userEmail}</p>
                    ) : null}
                  </div>
                </div>
                <button
                  ref={menuInitialFocusRef}
                  type="button"
                  aria-label="Zamknij menu"
                  onClick={onCloseMenu}
                  className="rounded-md p-2 text-primary-light-400 transition-colors hover:text-primary-light-100 focus-outline"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <nav
                className="flex flex-1 flex-col overflow-y-auto px-3 py-3"
                aria-label="Sekcje workspace serwisu"
              >
                <div className="space-y-5">
                  <section>
                    <p className="px-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-primary-light-400">
                      Serwis
                    </p>
                    <div className="mt-2 space-y-1.5">
                      {serviceItems.map((item) => {
                        const isActive = item.key === activeSection;
                        return (
                          <Link
                            key={item.key}
                            to={item.to}
                            aria-current={isActive ? 'page' : undefined}
                            onClick={onCloseMenu}
                            className={`${menuItemBaseClass} ${
                              isActive
                                ? 'bg-ratuj-300/10 text-primary-light-100'
                                : 'text-primary-light-300 hover:bg-primary-dark-300/35 hover:text-primary-light-100'
                            }`}
                          >
                            {isActive ? (
                              <span
                                aria-hidden="true"
                                className="absolute inset-y-2 left-0 w-1 rounded-r-full bg-ratuj-300"
                              />
                            ) : null}
                            <span className={isActive ? 'pl-3' : ''}>{item.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </section>

                  {communityItems.length > 0 ? (
                    <section>
                      <p className="px-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-primary-light-400">
                        Społeczność
                      </p>
                      <div className="mt-2 space-y-1.5">
                        {communityItems.map((item) => {
                          const isActive = item.key === activeSection;
                          return (
                            <Link
                              key={item.key}
                              to={item.to}
                              aria-current={isActive ? 'page' : undefined}
                              onClick={onCloseMenu}
                              className={`${menuItemBaseClass} ${
                                isActive
                                  ? 'bg-ratuj-300/10 text-primary-light-100'
                                  : 'text-primary-light-300 hover:bg-primary-dark-300/35 hover:text-primary-light-100'
                              }`}
                            >
                              {isActive ? (
                                <span
                                  aria-hidden="true"
                                  className="absolute inset-y-2 left-0 w-1 rounded-r-full bg-ratuj-300"
                                />
                              ) : null}
                              <span className={isActive ? 'pl-3' : ''}>{item.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </section>
                  ) : null}
                </div>
              </nav>

              <div className="border-t border-primary-dark-300/22 px-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-3">
                <p className="px-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-primary-light-400">
                  Wyjście
                </p>
                <Link
                  to={publicExitHref}
                  onClick={onCloseMenu}
                  className={`${menuItemBaseClass} mt-2 text-primary-light-300 hover:bg-primary-dark-300/35 hover:text-primary-light-100`}
                >
                  <span>Wróć do części publicznej</span>
                </Link>
              </div>
            </Modal>,
            document.body
          )
        : null}
    </>
  );
};

export default ServiceWorkspaceHeader;
